/**
 * Required-info gate. Mounted at `/profile/:pid/setup`. The
 * `ProfileLayoutResponsive` upstream redirects every other profile
 * route here when the profile lacks a `birthdate` — both for
 * freshly-linked profiles AND for older ones that pre-date the
 * gate. The parent can't proceed to the dashboard, the inbox,
 * or any other surface until birthdate + gender are saved.
 *
 * Birthdate is what the score aggregation uses for age-band
 * filtering (a HARMFUL-only-for-toddlers score shouldn't gate a
 * 12yo's dashboard). Gender is for content-policy preferences
 * we'll expose later. We collect both up-front so the parent
 * doesn't get gated again.
 */
import React, {useMemo, useState} from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {useNavigate, useParams} from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {ProfileGender} from '../../types/generated/graphql';
import {useGetMyYouTubeProfiles} from '../../api/graphql_profiles';
import {
  useGetProfilePreferences,
  useSetProfilePreferences,
} from '../../api/graphql_profile_preferences';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

function ageFromBirthdate(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const d = new Date(`${iso}T00:00:00Z`);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getUTCFullYear() - d.getUTCFullYear();
  const md = now.getUTCMonth() - d.getUTCMonth();
  if (md < 0 || (md === 0 && now.getUTCDate() < d.getUTCDate())) age--;
  return age;
}

export default function ProfileSetup({client}: Props): React.JSX.Element {
  const {pid} = useParams<{pid: string}>();
  const navigate = useNavigate();

  const {data: profilesData} = useGetMyYouTubeProfiles(client);
  const profile = useMemo(() => {
    const list = profilesData?.getMyYouTubeProfiles ?? [];
    return list.find(p => p?.id === pid) ?? null;
  }, [profilesData, pid]);
  const kidName = profile?.name?.trim() || 'this kid';

  const {data: prefsData, loading: prefsLoading} = useGetProfilePreferences(
    client,
    pid ?? null,
  );
  const [setPrefs, {loading: saving}] = useSetProfilePreferences(client);

  const [birthdate, setBirthdate] = useState<string>('');
  const [gender, setGender] = useState<ProfileGender | ''>('');
  const [error, setError] = useState<string | null>(null);

  // Hydrate from any partial state already on the profile (e.g.
  // birthdate set but gender skipped, or vice versa).
  React.useEffect(() => {
    const p = prefsData?.getProfilePreferences;
    if (!p) return;
    if (p.birthdate && !birthdate) setBirthdate(p.birthdate);
    if (p.gender && !gender) setGender(p.gender as ProfileGender);
    // intentionally exclude `birthdate` / `gender` from deps so
    // user edits aren't clobbered by re-fetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefsData]);

  const previewAge = useMemo(() => ageFromBirthdate(birthdate), [birthdate]);

  const handleSave = async () => {
    setError(null);
    if (!pid) return;
    if (!birthdate) {
      setError('Please pick a birthdate.');
      return;
    }
    const ageNow = ageFromBirthdate(birthdate);
    if (ageNow == null || ageNow < 0 || ageNow > 25) {
      setError(
        'That birthdate looks off — please re-check (we expect children under ~25).',
      );
      return;
    }
    if (!gender) {
      setError("Please pick a gender (or 'Prefer not to say').");
      return;
    }
    try {
      await setPrefs({
        variables: {
          profileId: pid,
          input: {birthdate, gender},
        },
      });
      // The gate in ProfileLayoutResponsive will let us through to
      // the dashboard now that birthdate is set.
      navigate(`/profile/${pid}`, {replace: true});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  };

  if (!pid) {
    return <Container sx={{py: 6}}>Missing profile id.</Container>;
  }

  return (
    <Box sx={{minHeight: '100vh', bgcolor: '#F8FAFC'}}>
      <Container maxWidth="sm" sx={{py: 6}}>
        <Typography sx={{fontSize: 12, fontWeight: 700, color: '#3448F0', textTransform: 'uppercase', letterSpacing: 0.6}}>
          One quick step
        </Typography>
        <Typography
          component="h1"
          sx={{mt: 1, fontSize: 28, fontWeight: 800, color: '#0F172A', lineHeight: 1.2}}
        >
          Tell us about {kidName}
        </Typography>
        <Typography sx={{mt: 1, fontSize: 14, color: '#475569', lineHeight: 1.55}}>
          GoodShowHQ uses age and a couple of preferences to score videos
          against {kidName}'s rubric, not the average kid's. We need a
          birthdate and a gender before we can show the dashboard.
        </Typography>

        {prefsLoading ? (
          <Typography sx={{mt: 4, fontSize: 13, color: '#64748B'}}>
            Loading…
          </Typography>
        ) : (
          <Stack spacing={3} sx={{mt: 4}}>
            <Box>
              <TextField
                fullWidth
                type="date"
                label="Birthdate"
                value={birthdate}
                onChange={e => setBirthdate(e.target.value)}
                InputLabelProps={{shrink: true}}
                inputProps={{max: new Date().toISOString().slice(0, 10)}}
                disabled={saving}
              />
              {previewAge != null && (
                <Typography sx={{mt: 0.5, fontSize: 12, color: '#64748B'}}>
                  That makes {kidName} {previewAge} year{previewAge === 1 ? '' : 's'} old.
                </Typography>
              )}
            </Box>

            <FormControl fullWidth disabled={saving}>
              <InputLabel id="setup-gender-label">Gender</InputLabel>
              <Select
                labelId="setup-gender-label"
                value={gender}
                label="Gender"
                onChange={e => setGender(e.target.value as ProfileGender)}
              >
                <MenuItem value={ProfileGender.Female}>Female</MenuItem>
                <MenuItem value={ProfileGender.Male}>Male</MenuItem>
                <MenuItem value={ProfileGender.NonBinary}>Non-binary</MenuItem>
                <MenuItem value={ProfileGender.PreferNotToSay}>
                  Prefer not to say
                </MenuItem>
              </Select>
            </FormControl>

            {error && <Alert severity="error">{error}</Alert>}

            <Button
              variant="contained"
              size="large"
              disabled={saving}
              onClick={handleSave}
              sx={{
                borderRadius: 999,
                bgcolor: '#3448F0',
                fontWeight: 700,
                textTransform: 'none',
                py: 1.25,
                '&:hover': {bgcolor: '#2839C9'},
              }}
            >
              {saving ? 'Saving…' : `Save & open ${kidName}'s dashboard`}
            </Button>

            <Typography sx={{fontSize: 12, color: '#94A3B8', textAlign: 'center'}}>
              You can change either of these later from the profile settings.
            </Typography>
          </Stack>
        )}
      </Container>
    </Box>
  );
}
